/**
 * renderer/board-frame.ts
 *
 * Pure board layout and camera math. No Pixi, no DOM.
 *
 * The camera covers the board cells only: `{ cellPx, x, y }` is the cell size
 * and the host-pixel position of the board's top-left corner. Clue headers and
 * footers are screen-space strips pinned beside the board; they are never part
 * of the zoomable world.
 *
 * Dependencies run one way:
 *
 *   cellPx → header thickness → pan limits → camera position → BoardFrame
 *
 * Header thickness depends only on zoom (never on position), so clamping a pan
 * can never change a header and nothing feeds back. Fit is a closed formula.
 */

import type { Point } from './board-renderer';

/** Largest cell size zooming in can reach, unless fitting the board needs more. */
export const MAX_CELL_PX = 96;
/** Narrowest clue slot, as a fraction of the cell size. */
export const MIN_CLUE_SQUISH = 0.35;
/** The squish grid: settings slider steps and mouse-wheel notches. */
export const CLUE_SQUISH_STEP = 0.05;
/** Default cap on the row header's width, as a fraction of the screen width. */
export const DEFAULT_ROW_HEADER_MAX = 0.5;
/** Default cap on the column header's height, as a fraction of the screen height. */
export const DEFAULT_COLUMN_HEADER_MAX = 0.5;

export interface FrameInput {
  containerWidth: number;
  containerHeight: number;
  rows: number;
  columns: number;
  /** Clue slots the row header needs (most clues in any row). */
  rowClueSlots: number;
  /** Clue slots the column header needs (most clues in any column). */
  columnClueSlots: number;
  /** Extra row header room (host px) for ticks standing for hidden clues (hide solved edge clues). */
  rowClueTickPx?: number;
  /** Extra column header room (host px) for ticks standing for hidden clues. */
  columnClueTickPx?: number;
  rowFooterSize: number;
  columnFooterSize: number;
  /** Row header width cap as a fraction of container width (1 = no cap). */
  rowHeaderMaxFraction: number;
  /** Column header height cap as a fraction of container height (1 = no cap). */
  columnHeaderMaxFraction: number;
  /** Row clue slot width as a fraction of the cell size. */
  rowSquish: number;
  /** Column clue slot height as a fraction of the cell size. */
  columnSquish: number;
  /**
   * Fold strips (host px) beside board edges whose solved lines are folded
   * away. They scroll with the board, between it and the headers/footers.
   */
  folds?: Folds;
}

export interface Folds {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export const NO_FOLDS: Folds = { top: 0, bottom: 0, left: 0, right: 0 };
/** Thickness of a fold strip (host px). */
export const FOLD_PX = 14;

function foldsOf(input: FrameInput): Folds {
  return input.folds ?? NO_FOLDS;
}

export interface Camera {
  /** Cell size in host pixels. */
  cellPx: number;
  /** Host-pixel X of the board's left edge. */
  x: number;
  /** Host-pixel Y of the board's top edge. */
  y: number;
}

/**
 * Everything screen-space components need, derived once per camera change.
 * All values are host (CSS) pixels.
 */
export interface BoardFrame {
  containerWidth: number;
  containerHeight: number;
  rows: number;
  columns: number;

  /** Board top-left corner. */
  x: number;
  y: number;
  cellPx: number;
  boardWidthPx: number;
  boardHeightPx: number;

  /** Fold strips beside the board (0 where nothing is folded). */
  folds: Folds;

  /** Row header strip: pinned left of the board, lines aligned with board rows. */
  rowHeaderLeftPx: number;
  rowHeaderWidthPx: number;
  /** Size of one row clue slot across the header (cell size × squish). Lines with more clues than fit scroll. */
  rowClueSlotPx: number;

  /** Column header strip: pinned above the board, lines aligned with board columns. */
  columnHeaderTopPx: number;
  columnHeaderHeightPx: number;
  /** Size of one column clue slot across the header (cell size × squish). Lines with more clues than fit scroll. */
  columnClueSlotPx: number;

  /** Row footer strip (right of the board, docked to the screen edge when the board runs past it). */
  rowFooterLeftPx: number;
  rowFooterSize: number;
  /** Column footer strip (below the board, docked to the screen edge when the board runs past it). */
  columnFooterTopPx: number;
  columnFooterSize: number;

  /** The part of the screen where board cells can show: inside the headers and footers. */
  viewLeftPx: number;
  viewTopPx: number;
  viewRightPx: number;
  viewBottomPx: number;
}

// ── Header thickness ─────────────────────────────────────────────────────────

/**
 * Header thickness: one slot (cell size × squish) per clue plus `tickPx` for
 * hidden clues' ticks, capped at a fraction of the screen but never thinner
 * than one slot.
 */
export function headerThickness(slots: number, slotPx: number, maxPx: number, tickPx = 0): number {
  if (slots <= 0) return 0;
  return Math.max(0, Math.min(slots * slotPx + tickPx, Math.max(maxPx, slotPx)));
}

function rowThickness(input: FrameInput, cellPx: number): number {
  return headerThickness(input.rowClueSlots, cellPx * input.rowSquish, rowHeaderMaxPx(input), input.rowClueTickPx);
}

function columnThickness(input: FrameInput, cellPx: number): number {
  return headerThickness(input.columnClueSlots, cellPx * input.columnSquish, columnHeaderMaxPx(input), input.columnClueTickPx);
}

function rowHeaderMaxPx(input: FrameInput): number {
  return input.rowHeaderMaxFraction * input.containerWidth;
}

function columnHeaderMaxPx(input: FrameInput): number {
  return input.columnHeaderMaxFraction * input.containerHeight;
}

// ── Zoom limits ──────────────────────────────────────────────────────────────

/**
 * Largest cell size along one axis at which header + board + footer fit.
 * The space used, `cells·cs + min(slots·s·cs + ticks, max(cap, s·cs))`, grows
 * with cs, and {min(A, max(B, C)) ≤ K} = {A ≤ K} ∪ ({B ≤ K} ∩ {C ≤ K}), so the
 * answer is max(uncapped root, min(capped root, one-slot root)). No iteration.
 */
function axisFitCellPx(available: number, cells: number, slots: number, squish: number, capPx: number, tickPx = 0): number {
  if (cells <= 0) return Infinity;
  if (slots <= 0) return available / cells;
  const uncapped = Math.max(0, available - tickPx) / (cells + slots * squish);
  const capped = (available - capPx) / cells;
  const oneSlot = available / (cells + squish);
  return Math.max(uncapped, Math.min(capped, oneSlot));
}

/** Cell size that fits the whole board with its headers and footers on screen. */
export function fitCellPx(input: FrameInput): number {
  const fx = axisFitCellPx(
    input.containerWidth - input.rowFooterSize - foldsOf(input).left - foldsOf(input).right,
    input.columns,
    input.rowClueSlots,
    input.rowSquish,
    rowHeaderMaxPx(input),
    input.rowClueTickPx,
  );
  const fy = axisFitCellPx(
    input.containerHeight - input.columnFooterSize - foldsOf(input).top - foldsOf(input).bottom,
    input.rows,
    input.columnClueSlots,
    input.columnSquish,
    columnHeaderMaxPx(input),
    input.columnClueTickPx,
  );
  const fit = Math.min(fx, fy);
  return Number.isFinite(fit) && fit > 0 ? fit : 1;
}

export function cellPxLimits(input: FrameInput): { min: number; max: number } {
  const min = fitCellPx(input);
  return { min, max: Math.max(min, MAX_CELL_PX) };
}

// ── Camera clamping ──────────────────────────────────────────────────────────

/**
 * Allowed board origin along one axis. When header + board + footer fit, the
 * group is centered. Otherwise the board may pan between its far edge meeting
 * the footer and its near edge meeting the header.
 */
function clampAxis(
  pos: number,
  container: number,
  header: number,
  board: number,
  footer: number,
  foldNear = 0,
  foldFar = 0,
): number {
  // Fold strips travel with the board: clamp the board plus its folds, then step inside the near fold.
  const extent = foldNear + board + foldFar;
  const total = header + extent + footer;
  if (total <= container) return (container - total) / 2 + header + foldNear;
  const min = container - footer - extent;
  return Math.min(header, Math.max(min, pos - foldNear)) + foldNear;
}

export function clampCamera(input: FrameInput, camera: Camera): Camera {
  const { min, max } = cellPxLimits(input);
  const cellPx = Math.min(max, Math.max(min, camera.cellPx));
  const rowHeader = rowThickness(input, cellPx);
  const columnHeader = columnThickness(input, cellPx);
  return {
    cellPx,
    x: clampAxis(camera.x, input.containerWidth, rowHeader, input.columns * cellPx, input.rowFooterSize, foldsOf(input).left, foldsOf(input).right),
    y: clampAxis(camera.y, input.containerHeight, columnHeader, input.rows * cellPx, input.columnFooterSize, foldsOf(input).top, foldsOf(input).bottom),
  };
}

export function fitCamera(input: FrameInput): Camera {
  return clampCamera(input, { cellPx: fitCellPx(input), x: 0, y: 0 });
}

/**
 * The frame input with clue headers and footers scaled by `chrome` (1 = as
 * usual, 0 = gone, so the board can fill the container). Scaling squish and
 * the header cap scales the header thickness, so the fit and clamping follow;
 * at 0 the headers have no slots at all.
 */
export function withChrome(input: FrameInput, chrome: number): FrameInput {
  const c = Math.max(0, Math.min(1, chrome));
  if (c === 1) return input;
  if (c === 0) {
    return { ...input, rowClueSlots: 0, columnClueSlots: 0, rowClueTickPx: 0, columnClueTickPx: 0, rowFooterSize: 0, columnFooterSize: 0 };
  }
  return {
    ...input,
    rowClueTickPx: (input.rowClueTickPx ?? 0) * c,
    columnClueTickPx: (input.columnClueTickPx ?? 0) * c,
    rowFooterSize: input.rowFooterSize * c,
    columnFooterSize: input.columnFooterSize * c,
    rowHeaderMaxFraction: input.rowHeaderMaxFraction * c,
    columnHeaderMaxFraction: input.columnHeaderMaxFraction * c,
    rowSquish: input.rowSquish * c,
    columnSquish: input.columnSquish * c,
  };
}

/**
 * The camera `t` (0–1) of the way from `from` to `to`. Moving the origin at
 * the same rate as the cell size is a zoom about one fixed screen point.
 */
export function lerpCamera(from: Camera, to: Camera, t: number): Camera {
  return {
    cellPx: from.cellPx + (to.cellPx - from.cellPx) * t,
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  };
}

export function panCamera(input: FrameInput, camera: Camera, dx: number, dy: number): Camera {
  return clampCamera(input, { ...camera, x: camera.x + dx, y: camera.y + dy });
}

/** Zoom by `factor` keeping the board point under `focal` fixed (as far as the limits allow). */
export function zoomCamera(input: FrameInput, camera: Camera, focal: Point, factor: number): Camera {
  const { min, max } = cellPxLimits(input);
  const cellPx = Math.min(max, Math.max(min, camera.cellPx * factor));
  const ratio = cellPx / camera.cellPx;
  return clampCamera(input, {
    cellPx,
    x: focal.x - (focal.x - camera.x) * ratio,
    y: focal.y - (focal.y - camera.y) * ratio,
  });
}

/** Pan the least amount that brings the cell fully inside the visible board window. */
export function revealCell(input: FrameInput, camera: Camera, row: number, column: number): Camera {
  const frame = computeFrame(input, camera);
  const left = frame.x + column * frame.cellPx;
  const top = frame.y + row * frame.cellPx;
  const right = left + frame.cellPx;
  const bottom = top + frame.cellPx;

  let dx = 0;
  let dy = 0;
  if (left < frame.viewLeftPx) dx = frame.viewLeftPx - left;
  else if (right > frame.viewRightPx) dx = frame.viewRightPx - right;
  if (top < frame.viewTopPx) dy = frame.viewTopPx - top;
  else if (bottom > frame.viewBottomPx) dy = frame.viewBottomPx - bottom;

  return dx === 0 && dy === 0 ? camera : panCamera(input, camera, dx, dy);
}

// ── Frame ────────────────────────────────────────────────────────────────────

/** Derive every screen-space rect from a (clamped) camera. */
export function computeFrame(input: FrameInput, camera: Camera): BoardFrame {
  const { cellPx, x, y } = camera;
  const { containerWidth, containerHeight, rows, columns, rowFooterSize, columnFooterSize } = input;

  const boardWidthPx = columns * cellPx;
  const boardHeightPx = rows * cellPx;
  const rowHeaderWidthPx = rowThickness(input, cellPx);
  const columnHeaderHeightPx = columnThickness(input, cellPx);

  const folds = foldsOf(input);
  // Headers sit against the board (and its folds), and stay pinned to the screen edge once the board pans past it.
  const rowHeaderLeftPx = Math.max(0, x - folds.left - rowHeaderWidthPx);
  const columnHeaderTopPx = Math.max(0, y - folds.top - columnHeaderHeightPx);
  // Footers sit against the board (and its folds), and dock to the screen edge once the board runs past it.
  const rowFooterLeftPx = Math.min(x + boardWidthPx + folds.right, containerWidth - rowFooterSize);
  const columnFooterTopPx = Math.min(y + boardHeightPx + folds.bottom, containerHeight - columnFooterSize);

  return {
    containerWidth,
    containerHeight,
    rows,
    columns,
    x,
    y,
    cellPx,
    boardWidthPx,
    boardHeightPx,
    folds,
    rowHeaderLeftPx,
    rowHeaderWidthPx,
    rowClueSlotPx: cellPx * input.rowSquish,
    columnHeaderTopPx,
    columnHeaderHeightPx,
    columnClueSlotPx: cellPx * input.columnSquish,
    rowFooterLeftPx,
    rowFooterSize,
    columnFooterTopPx,
    columnFooterSize,
    viewLeftPx: rowHeaderLeftPx + rowHeaderWidthPx,
    viewTopPx: columnHeaderTopPx + columnHeaderHeightPx,
    viewRightPx: rowFooterLeftPx,
    viewBottomPx: columnFooterTopPx,
  };
}

// ── Header resizing ──────────────────────────────────────────────────────────

/** Smallest header a drag can leave (host px). */
export const MIN_DRAGGED_HEADER_PX = 14;

export interface HeaderCaps {
  /** Row header cap as a fraction of container width (1 = no cap). */
  row: number;
  /** Column header cap as a fraction of container height (1 = no cap). */
  column: number;
}

/**
 * Caps that give headers of the requested size. Dragging within 2px of (or past)
 * a header's natural size removes its cap, so "full size" is easy to reach.
 */
export function headerCapsForSize(input: FrameInput, cellPx: number, rowWidth: number, columnHeight: number): HeaderCaps {
  const naturalRow = input.rowClueSlots * cellPx * input.rowSquish + (input.rowClueTickPx ?? 0);
  const naturalColumn = input.columnClueSlots * cellPx * input.columnSquish + (input.columnClueTickPx ?? 0);
  const cap = (size: number, natural: number, container: number) => {
    const px = Math.max(MIN_DRAGGED_HEADER_PX, size);
    if (px >= natural - 2 || container <= 0) return 1;
    return Math.min(0.95, Math.max(0.04, px / container));
  };
  return {
    row: cap(rowWidth, naturalRow, input.containerWidth),
    column: cap(columnHeight, naturalColumn, input.containerHeight),
  };
}

// ── Hit testing ──────────────────────────────────────────────────────────────

/** How far from the headers' inner corner (host px) the resize grip reaches. */
export const GRIP_PX = 26;

export type FrameHit =
  | { kind: 'cell'; row: number; column: number }
  | { kind: 'row-header'; line: number }
  | { kind: 'column-header'; line: number }
  | { kind: 'grip' }
  | { kind: 'fold'; side: keyof Folds }
  | { kind: 'corner' }
  | { kind: 'footer' }
  | { kind: 'none' };

/** Which part of the board chrome is under a host-pixel point. */
export function hitTestFrame(frame: BoardFrame, point: Point): FrameHit {
  const { x: px, y: py } = point;
  const inBoardColumns = px >= frame.x && px < frame.x + frame.boardWidthPx;
  const inBoardRows = py >= frame.y && py < frame.y + frame.boardHeightPx;
  const leftOfView = px < frame.viewLeftPx;
  const aboveView = py < frame.viewTopPx;

  if (px >= frame.viewRightPx || py >= frame.viewBottomPx) {
    const inFooter = (px >= frame.viewRightPx && px < frame.viewRightPx + frame.rowFooterSize && inBoardRows)
      || (py >= frame.viewBottomPx && py < frame.viewBottomPx + frame.columnFooterSize && inBoardColumns);
    return inFooter ? { kind: 'footer' } : { kind: 'none' };
  }
  if (leftOfView && aboveView) {
    const inCorner = px >= frame.rowHeaderLeftPx && py >= frame.columnHeaderTopPx;
    if (!inCorner) return { kind: 'none' };
    const nearInner = frame.viewLeftPx - px <= GRIP_PX && frame.viewTopPx - py <= GRIP_PX;
    return nearInner ? { kind: 'grip' } : { kind: 'corner' };
  }
  if (leftOfView) {
    if (px < frame.rowHeaderLeftPx || !inBoardRows) return { kind: 'none' };
    return { kind: 'row-header', line: Math.floor((py - frame.y) / frame.cellPx) };
  }
  if (aboveView) {
    if (py < frame.columnHeaderTopPx || !inBoardColumns) return { kind: 'none' };
    return { kind: 'column-header', line: Math.floor((px - frame.x) / frame.cellPx) };
  }
  if (!inBoardColumns || !inBoardRows) {
    const f = frame.folds;
    if (inBoardColumns && f.top > 0 && py >= frame.y - f.top && py < frame.y) return { kind: 'fold', side: 'top' };
    if (inBoardColumns && f.bottom > 0 && py >= frame.y + frame.boardHeightPx && py < frame.y + frame.boardHeightPx + f.bottom) return { kind: 'fold', side: 'bottom' };
    if (inBoardRows && f.left > 0 && px >= frame.x - f.left && px < frame.x) return { kind: 'fold', side: 'left' };
    if (inBoardRows && f.right > 0 && px >= frame.x + frame.boardWidthPx && px < frame.x + frame.boardWidthPx + f.right) return { kind: 'fold', side: 'right' };
    return { kind: 'none' };
  }
  return {
    kind: 'cell',
    row: Math.floor((py - frame.y) / frame.cellPx),
    column: Math.floor((px - frame.x) / frame.cellPx),
  };
}
